import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import { Delete as DeleteIcon, Warning as WarningIcon } from '@mui/icons-material';
import { SchedulerConfig } from '@/types/smsScheduler';
import { formatErrorForDisplay } from '@/utils/errorUtils';

interface SchedulerDeleteDialogProps {
  open: boolean;
  config: SchedulerConfig | null;
  onClose: () => void;
  onConfirm: () => void;
  loading?: boolean;
  error?: string | null;
}

const SchedulerDeleteDialog: React.FC<SchedulerDeleteDialogProps> = ({
  open,
  config,
  onClose,
  onConfirm,
  loading = false,
  error = null
}) => {
  if (!config) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteIcon color="error" />
          <Typography variant="h6" component="span">
            Delete Scheduler Configuration
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {formatErrorForDisplay(error)}
          </Alert>
        )}

        <Alert severity="warning" sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <WarningIcon sx={{ mt: 0.5 }} />
            <Box>
              <Typography variant="body2" fontWeight="medium">
                This action cannot be undone
              </Typography>
              <Typography variant="body2">
                Deleting this scheduler configuration will permanently remove it and 
                stop all scheduled SMS notifications for this configuration.
              </Typography>
            </Box>
          </Box>
        </Alert>

        <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Configuration Details:
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Name:</strong> {config.schedule_name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Days Before RED:</strong> {config.days_before_red} days
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Time:</strong> {config.time_of_day}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Status:</strong> {config.is_active ? 'Active' : 'Inactive'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Created By:</strong> {config.created_by || 'System'}
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary">
          Are you sure you want to delete this scheduler configuration? 
          This will immediately stop the scheduler and remove all associated settings.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={onClose}
          disabled={loading}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          disabled={loading}
          color="error"
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} /> : <DeleteIcon />}
        >
          {loading ? 'Deleting...' : 'Delete Configuration'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SchedulerDeleteDialog;

